import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { FeatureFlagsService } from '../featureFlags/feature-flags.service';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import { ActivitySource } from 'src/app/models/api/agentStep.model';
import { ISelectedFilters } from 'src/app/models/filters/selectedFilters.interface';
import { ToggleOption } from 'src/app/components/option-toggle/option-toggle.component';

/** URL query parameter name for source toggle */
export const SOURCE_QUERY_PARAM = 'source';

/**
 * Creates a callback wrapper that skips the first invocation.
 * Useful for effects that should not run on initial render.
 * @param callback - The callback to wrap
 * @returns A wrapped callback that skips the first call
 */
export function skipFirstRun<T>(callback: (value: T) => void): (value: T) => void {
  let isFirstRun = true;
  return (value: T) => {
    if (isFirstRun) {
      isFirstRun = false;
      return;
    }
    callback(value);
  };
}

/** Default toggle option (Audit Log) */
export const DEFAULT_SOURCE_TOGGLE: ToggleOption = 'option1';

/** Mapping from toggle option to URL query param value (user-facing in URL) */
export const SOURCE_TOGGLE_TO_QUERY_PARAM: Record<ToggleOption, string> = {
  option1: 'auditlog',
  option2: 'inline',
};

/** Mapping from URL query param value to toggle option */
export const SOURCE_QUERY_PARAM_TO_TOGGLE: Record<string, ToggleOption> = {
  auditlog: 'option1',
  inline: 'option2',
};

/**
 * Service to manage unified pipeline behavior for pages with Audit Log/In-Line toggle.
 * This service provides reusable logic for:
 * - Checking if unified pipeline feature flag is enabled
 * - Getting the appropriate source filter based on toggle selection
 * - Applying source filter to selected filters
 *
 * Can be used by Activity page, Findings page, or any other page that needs
 * the Audit Log/In-Line toggle with unified endpoint support.
 */
@Injectable({
  providedIn: 'root',
})
export class UnifiedPipelineService {
  constructor(
    private readonly _featureFlagsService: FeatureFlagsService,
    private readonly _router: Router
  ) {}

  /**
   * Check if the unified pipeline feature flag is enabled
   */
  public isEnabled(): boolean {
    return this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.UnifiedPipeline);
  }

  /**
   * Get the ActivitySource value based on the toggle selection
   * @param toggleOption - 'option1' for Audit Log, 'option2' for In-Line
   */
  public getSourceFromToggle(toggleOption: ToggleOption): ActivitySource {
    return toggleOption === 'option1' ? ActivitySource.auditlog : ActivitySource.inline;
  }

  /**
   * Get the toggle option based on the ActivitySource value
   * @param source - ActivitySource value
   */
  public getToggleFromSource(source: ActivitySource): ToggleOption {
    return source === ActivitySource.auditlog ? 'option1' : 'option2';
  }

  /**
   * Apply source filter to the selected filters based on toggle selection.
   * Only applies the filter when unified pipeline is enabled.
   *
   * @param selectedFilters - The current selected filters
   * @param toggleOption - Current toggle selection ('option1' for Audit Log, 'option2' for In-Line)
   * @returns Modified filters with source filter applied (if unified pipeline enabled)
   */
  public applySourceFilter(selectedFilters: ISelectedFilters, toggleOption: ToggleOption): ISelectedFilters {
    if (toggleOption === 'option2' && !this.isEnabled()) {
      return selectedFilters;
    }

    return {
      ...selectedFilters,
      source_pipeline: [this.getSourceFromToggle(toggleOption)],
    };
  }

  /**
   * Check if the current toggle should use the unified (Audit Log) endpoint.
   * When unified pipeline is enabled, both Audit Log and In-Line use the same endpoint.
   * When disabled, only Audit Log (option1) uses the Audit Log endpoint.
   *
   * @param toggleOption - Current toggle selection
   * @returns true if should use unified/Audit Log endpoint
   */
  public shouldUseUnifiedEndpoint(toggleOption: ToggleOption): boolean {
    return this.isEnabled() || toggleOption === 'option1';
  }

  /**
   * Check if the legacy In-Line endpoint should be used.
   * Only used when unified pipeline is disabled and In-Line toggle is selected.
   *
   * @param toggleOption - Current toggle selection
   * @returns true if should use legacy In-Line endpoint
   */
  public shouldUseLegacyInLineEndpoint(toggleOption: ToggleOption): boolean {
    return !this.isEnabled() && toggleOption === 'option2';
  }

  /**
   * Get the toggle option from URL query parameter.
   * Returns the default (Audit Log) if no valid source param is present.
   */
  public getToggleFromUrl(): ToggleOption {
    const url = new URL(window.location.href);
    const sourceParam = url.searchParams.get(SOURCE_QUERY_PARAM);
    return SOURCE_QUERY_PARAM_TO_TOGGLE[sourceParam ?? ''] ?? DEFAULT_SOURCE_TOGGLE;
  }

  /**
   * Check if the source query parameter is present in the URL.
   */
  public hasSourceParam(): boolean {
    const url = new URL(window.location.href);
    return url.searchParams.has(SOURCE_QUERY_PARAM);
  }

  /**
   * Update the source query parameter in the URL.
   * @param toggleOption - The toggle option to set
   * @param replaceUrl - Whether to replace the URL in history (default: false)
   */
  public updateSourceInUrl(toggleOption: ToggleOption, replaceUrl = false): void {
    this._router.navigate([], {
      queryParams: { [SOURCE_QUERY_PARAM]: SOURCE_TOGGLE_TO_QUERY_PARAM[toggleOption] },
      queryParamsHandling: 'merge',
      replaceUrl,
    });
  }

  /**
   * Initialize the source toggle from URL or set default.
   * Call this in ngOnInit when unified pipeline is enabled.
   * @returns The toggle option to use
   */
  public initializeSourceToggle(): ToggleOption {
    if (!this.hasSourceParam()) {
      this.updateSourceInUrl(DEFAULT_SOURCE_TOGGLE, true);
    }
    return this.getToggleFromUrl();
  }
}
